import colors from "assets/colors";
import { Chart, registerables } from "chart.js";
import BoxContainer from "components/BoxContainer";
import PageContainer from "components/PageContainer";
import { useLocationParams } from "hooks/location";
import { CSSProperties, useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import {
  Divider,
  Dropdown,
  DropdownProps,
  Grid,
  Header,
  Loader,
} from "semantic-ui-react";
import useIntegration, {
  IntegrationClassIDState,
  IntegrationYearIDState,
} from "./integration-hooks";
import { IntegrationPayload } from "./types";

const HeaderStyle: CSSProperties = {
  color: colors.darkBlue,
  padding: "1em",
  margin: 0,
};

const DividerStyle: CSSProperties = {
  margin: 0,
};

const ChartContainerStyle: CSSProperties = {
  minHeight: 500,
  height: 600,
  position: "relative",
};

const GraphContainerStyle: CSSProperties = {
  padding: "1em",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const ErrorInfoSpanStyle: CSSProperties = {
  color: "red",
  padding: "1em",
};

const IntegrationPage = () => {
  const { data, error, yearOptions, classOptions, isLoading } =
    useIntegration();
  const [yearID, setYearID] = useRecoilState(IntegrationYearIDState);
  const [classID, setClassID] = useRecoilState(IntegrationClassIDState);
  useLocationParams();

  function onYearIDChange(_: any, data: DropdownProps) {
    setYearID(data.value as string);
  }

  function onClassIDChange(_: any, data: DropdownProps) {
    setClassID(data.value as string);
  }

  return (
    <PageContainer title="AI 綜合分析">
      <Grid columns={1}>
        <Grid.Column>
          <BoxContainer>
            <Header content="AI 綜合分析" style={HeaderStyle} />
            <Divider style={DividerStyle} />
            <div style={GraphContainerStyle}>
              年級:
              <Dropdown
                options={yearOptions.map((year) => ({
                  key: year.year_id,
                  text: year.year_id,
                  value: year.year_id,
                }))}
                value={yearID}
                onChange={onYearIDChange}
                placeholder="年級"
                style={{ marginRight: "1em" }}
              />
              班別:
              <Dropdown
                options={classOptions.map((cls) => ({
                  key: cls.class_id,
                  text: cls.class_name,
                  value: cls.class_id,
                }))}
                value={classID}
                onChange={onClassIDChange}
                placeholder="班別"
              />
            </div>
            <div style={ErrorInfoSpanStyle}>{error}</div>
            <div style={ChartContainerStyle}>
              <Loader active={isLoading} />
              {data && <ScatterGraph data={data} />}
            </div>
          </BoxContainer>
        </Grid.Column>
      </Grid>
    </PageContainer>
  );
};

const ScatterGraph = ({ data }: { data?: IntegrationPayload }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (chartRef && chartRef.current && data) {
      Chart.register(...registerables);
      const chart = new Chart(chartRef.current, {
        type: "scatter",
        data: {
          datasets: data.speed_score_data.map((dataset, i) => {
            return {
              label: dataset.name,
              pointRadius: 4,
              pointStyle: "circle",
              backgroundColor: colors.distinct[i % colors.distinct.length],
              borderColor: colors.distinct[i % colors.distinct.length],
              fill: colors.distinct[i % colors.distinct.length],
              data: dataset.values.map((value) => {
                return {
                  x: parseTimeString(value.speed),
                  y: value.score,
                  label: value.label,
                };
              }),
            };
          }),
        },
        options: {
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  const x = context.parsed.x;
                  const y = context.parsed.y;
                  const label = (
                    context.dataset.data[context.dataIndex] as any
                  )["label"];
                  return `${label}\n使用時間: ${parseTimeToString(
                    x
                  )}\n成績（題）: ${y}`;
                },
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxis: {
              title: {
                text: "使用時間",
                display: true,
              },
              ticks: {
                callback: (tickValue) => {
                  return parseTimeToString(tickValue);
                },
              },
            },
            yAxis: {
              title: {
                text: "成績（題）",
                display: true,
              },
            },
          },
        },
      });
      return () => {
        chart.destroy();
      };
    }
  }, [data]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        padding: "1em",
      }}
    >
      <canvas ref={chartRef} />
    </div>
  );
};

function parseTimeString(str: string): number {
  const [hStr, mStr, sStr] = str.split(":");
  if (!hStr || !mStr || !sStr) return 0;
  const hour = parseInt(hStr);
  const min = parseInt(mStr);
  const sec = parseInt(sStr);
  const duration = sec + min * 60 + hour * 60 * 60;
  return duration;
}

function parseTimeToString(value: string | number) {
  if (typeof value === "number") {
    const h = Math.floor(value / 3600);
    const m = Math.floor((value % 3600) / 60);
    const s = Math.floor((value % 3600) % 60);
    return `${padLeadingZeros(h, 2)}:${padLeadingZeros(m, 2)}:${padLeadingZeros(
      s,
      2
    )}`;
  } else {
    const num = parseInt(value);
    const h = Math.floor(num / 3600);
    const m = Math.floor((num % 3600) / 60);
    const s = Math.floor((num % 3600) % 60);
    return `${padLeadingZeros(h, 2)}:${padLeadingZeros(m, 2)}:${padLeadingZeros(
      s,
      2
    )}`;
  }
}

function padLeadingZeros(num: number, size: number) {
  var s = num + "";
  while (s.length < size) s = "0" + s;
  return s;
}

export default IntegrationPage;
